import { LookbookBrand, LookbookDesign, LookbookDesignProps } from '@/types/activation/lookbook';
import { BrandMark, LookbookQR, PoweredByPill } from './LookbookPrimitives';

const INK = '#15233A';
const MUTED = '#5B6472';
const targetLabel = (brand: LookbookBrand) =>
  brand.targetType === 'portal' ? 'help portal' : 'Genie';

// ---- colour helpers ----------------------------------------------------------
const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
const hexToRgb = (hex: string) => {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return { r: parseInt(v.slice(0, 2), 16), g: parseInt(v.slice(2, 4), 16), b: parseInt(v.slice(4, 6), 16) };
};
export const darken = (hex: string, amt = 0.2) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgb(${clamp(r * (1 - amt))}, ${clamp(g * (1 - amt))}, ${clamp(b * (1 - amt))})`;
};
export const brandGradient = (b: LookbookBrand, angle = 160) =>
  `linear-gradient(${angle}deg, ${b.primary}, ${darken(b.primary, 0.28)})`;

// ---- small bits --------------------------------------------------------------
const Eyebrow = ({ color, children }: { color: string; children: React.ReactNode }) => (
  <div style={{ color, fontWeight: 700, letterSpacing: '0.2em', fontSize: 12, textTransform: 'uppercase' }}>
    {children}
  </div>
);

const cardShadow = '0 24px 55px rgba(15,23,42,.18)';
const panelShadow = '0 26px 60px rgba(15,23,42,.30)';

// ============================================================================
// Placement designs
// ============================================================================

const WindowCling = ({ brand }: LookbookDesignProps) => (
  <div
    style={{
      width: 430, height: 430, borderRadius: '50%', background: '#fff',
      border: `12px solid ${brand.primary}`, boxShadow: cardShadow,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14,
    }}
  >
    <Eyebrow color={brand.secondary}>Open · Scan me</Eyebrow>
    <LookbookQR brand={brand} size={158} />
    <div style={{ color: brand.primary, fontWeight: 700, fontSize: 18 }}>Just scan</div>
  </div>
);

const CounterStandee = ({ brand }: LookbookDesignProps) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <div
      style={{
        width: 330, background: '#fff', borderRadius: '18px 18px 6px 6px', boxShadow: '0 18px 44px rgba(15,23,42,.16)',
        border: '1.5px solid #ECEFF4', padding: '30px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
      }}
    >
      <Eyebrow color={brand.secondary}>{brand.name}</Eyebrow>
      <div style={{ color: INK, fontWeight: 800, fontSize: 26, textAlign: 'center', lineHeight: 1.05 }}>Start here.</div>
      <LookbookQR brand={brand} size={150} />
      <div style={{ color: MUTED, fontSize: 13, textAlign: 'center' }}>Scan to open {brand.name} on your phone.</div>
    </div>
    <div style={{ width: 250, height: 26, background: '#E6EAF0', borderRadius: '0 0 30px 30px', boxShadow: '0 16px 24px rgba(15,23,42,.14)' }} />
  </div>
);

const TableTent = ({ brand }: LookbookDesignProps) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <svg width={344} height={44} viewBox="0 0 344 44" style={{ display: 'block' }}>
      <polygon points="172,0 344,44 0,44" fill={brand.primary} />
    </svg>
    <div
      style={{
        width: 344, background: '#fff', border: `2px solid ${brand.primary}`, borderTop: 'none', borderRadius: '0 0 14px 14px',
        boxShadow: cardShadow, padding: '24px 26px 28px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
      }}
    >
      <div style={{ color: INK, fontWeight: 800, fontSize: 22, textAlign: 'center' }}>Scan &amp; open {brand.name}</div>
      <LookbookQR brand={brand} size={148} />
      <div style={{ color: MUTED, fontSize: 13 }}>Start from your phone.</div>
    </div>
  </div>
);

const VehicleDecal = ({ brand }: LookbookDesignProps) => (
  <div
    style={{
      width: 560, height: 232, background: brandGradient(brand, 135), borderRadius: 16, boxShadow: panelShadow,
      display: 'flex', alignItems: 'center', gap: 26, padding: '24px 30px',
    }}
  >
    <LookbookQR brand={brand} size={172} />
    <div style={{ flex: 1 }}>
      <div style={{ color: '#fff', fontWeight: 800, fontSize: 34, lineHeight: 1 }}>Scan from<br />the kerb.</div>
      <div style={{ color: 'rgba(255,255,255,.85)', fontWeight: 600, fontSize: 15, marginTop: 12 }}>Open {brand.name} on your phone</div>
    </div>
  </div>
);

const FridgeMagnet = ({ brand }: LookbookDesignProps) => (
  <div
    style={{
      width: 340, height: 340, borderRadius: 24, background: '#fff', boxShadow: cardShadow, overflow: 'hidden',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}
  >
    <div style={{ width: '100%', padding: 16, background: `${brand.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
      <BrandMark brand={brand} size={26} radius={6} />
      <span style={{ fontWeight: 700, fontSize: 16, color: INK }}>{brand.name}</span>
    </div>
    <div style={{ marginTop: 18 }}><LookbookQR brand={brand} size={150} /></div>
    <div style={{ marginTop: 'auto', width: '100%', padding: 16, background: brand.primary, color: '#fff', textAlign: 'center', fontWeight: 700, fontSize: 17 }}>
      Your {targetLabel(brand)}, one scan away.
    </div>
  </div>
);

const BusinessCard = ({ brand }: LookbookDesignProps) => (
  <div style={{ width: 480, height: 290, borderRadius: 16, boxShadow: cardShadow, display: 'flex', overflow: 'hidden', background: '#fff' }}>
    <div style={{ width: 186, background: `${brand.primary}0A`, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1.5px solid #EEF1F5' }}>
      <LookbookQR brand={brand} size={140} pad={10} />
    </div>
    <div style={{ flex: 1, padding: '30px 28px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <BrandMark brand={brand} size={32} />
        <span style={{ fontWeight: 800, fontSize: 20, color: INK }}>{brand.name}</span>
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: brand.primary, lineHeight: 1.3 }}>Scan to open {brand.name}.</div>
      <div style={{ fontSize: 13, color: MUTED }}>helpgenie.ai</div>
    </div>
  </div>
);

const Sticker = ({ brand }: LookbookDesignProps) => (
  <div
    style={{
      width: 380, borderRadius: 26, border: `3px solid ${brand.primary}`, background: '#fff', boxShadow: cardShadow,
      overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, paddingTop: 26,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <BrandMark brand={brand} size={26} radius={6} />
      <span style={{ fontWeight: 700, fontSize: 17, color: INK }}>{brand.name}</span>
    </div>
    <LookbookQR brand={brand} size={184} />
    <div style={{ width: '100%', background: brand.secondary, color: '#fff', fontWeight: 800, letterSpacing: '0.1em', fontSize: 17, textAlign: 'center', padding: 16 }}>
      NEED HELP? SCAN ME
    </div>
  </div>
);

const YardSign = ({ brand }: LookbookDesignProps) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
    <div
      style={{
        width: 330, height: 460, background: brandGradient(brand), borderRadius: 14, boxShadow: panelShadow,
        display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '30px 24px',
      }}
    >
      <BrandMark brand={brand} size={42} radius={12} />
      <div style={{ color: '#fff', fontWeight: 800, fontSize: 40, lineHeight: 0.98, textAlign: 'center', marginTop: 12 }}>Got<br />questions?</div>
      <div style={{ color: 'rgba(255,255,255,.85)', fontWeight: 600, fontSize: 15, margin: '12px 0 18px', textAlign: 'center' }}>Open {brand.name} from this QR.</div>
      <LookbookQR brand={brand} size={150} />
      <div style={{ color: 'rgba(255,255,255,.8)', fontSize: 11, marginTop: 14, letterSpacing: '0.04em' }}>Scan to start</div>
    </div>
    <div style={{ width: 12, height: 56, background: '#C9D0DA', borderRadius: 3 }} />
  </div>
);

const PullUpBanner = ({ brand }: LookbookDesignProps) => (
  <div
    style={{
      width: 270, height: 560, background: brandGradient(brand), borderRadius: 12, boxShadow: panelShadow,
      display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 26px',
    }}
  >
    <BrandMark brand={brand} size={52} radius={14} />
    <div style={{ color: '#fff', fontWeight: 800, fontSize: 26, lineHeight: 1.1, textAlign: 'center', margin: '18px 0 24px' }}>Meet {brand.name}.</div>
    <LookbookQR brand={brand} size={172} />
    <div style={{ color: 'rgba(255,255,255,.85)', fontWeight: 600, fontSize: 15, marginTop: 22, textAlign: 'center' }}>Scan to open on your phone</div>
    <div style={{ marginTop: 'auto' }}><PoweredByPill /></div>
  </div>
);

// ============================================================================
// Registry (caption metadata mirrors the Vital Energy lookbook spec lines)
// ============================================================================

export const LOOKBOOK_DESIGNS: LookbookDesign[] = [
  { id: 'window-cling', name: 'Window Cling', meta: '150mm circle · static cling · no adhesive', Component: WindowCling },
  { id: 'counter-standee', name: 'Counter Standee', meta: 'A6 · folded 350gsm card · table-top', Component: CounterStandee },
  { id: 'table-tent', name: 'Table Tent', meta: '100 x 150mm · folded card · two-sided', Component: TableTent },
  { id: 'vehicle-decal', name: 'Vehicle Decal', meta: '300 x 150mm · cut vinyl · outdoor grade', Component: VehicleDecal },
  { id: 'fridge-magnet', name: 'Fridge Magnet', meta: '80 x 80mm · magnetic vinyl', Component: FridgeMagnet },
  { id: 'business-card', name: 'Business Card', meta: '90 x 55mm · 400gsm matte · soft-touch', Component: BusinessCard },
  { id: 'sticker', name: 'Sticker', meta: '90 x 90mm · gloss vinyl · weatherproof', Component: Sticker },
  { id: 'yard-sign', name: 'Yard Sign', meta: '400 x 600mm · corflute + ground stake', Component: YardSign },
  { id: 'pull-up-banner', name: 'Pull-up Banner', meta: '850 x 2000mm · roller banner + base', Component: PullUpBanner },
];

// ============================================================================
// Cover + closing (full-page, PDF chrome)
// ============================================================================

export const LookbookCover = ({ brand }: LookbookDesignProps) => (
  <div style={{ width: '100%', height: '100%', background: brandGradient(brand, 145), display: 'flex', flexDirection: 'column', padding: 64, color: '#fff', position: 'relative', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: -120, right: -120, width: 420, height: 420, borderRadius: '50%', background: 'rgba(255,255,255,.10)' }} />
    <div style={{ marginTop: 40 }}>
      <div style={{ width: 96, height: 96, borderRadius: 24, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 14px 30px rgba(0,0,0,.18)' }}>
        <BrandMark brand={brand} size={64} radius={16} />
      </div>
    </div>
    <div style={{ marginTop: 36, fontWeight: 700, letterSpacing: '0.24em', fontSize: 14, textTransform: 'uppercase', opacity: 0.85 }}>Activation Lookbook</div>
    <h1 style={{ fontSize: 56, lineHeight: 1.03, fontWeight: 800, letterSpacing: '-0.02em', margin: '14px 0 18px' }}>Pick your designs.<br />We brand them.</h1>
    <p style={{ fontSize: 19, lineHeight: 1.55, maxWidth: '28em', opacity: 0.92 }}>
      A set of print-ready ways to put {brand.name} in front of customers. Every code opens your {targetLabel(brand)} from a phone.
    </p>
    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 14, paddingTop: 28, borderTop: '2px solid rgba(255,255,255,.25)' }}>
      <span style={{ fontWeight: 800, fontSize: 22 }}>{brand.name}</span>
      <PoweredByPill />
    </div>
  </div>
);

export const LookbookClosing = ({ brand }: LookbookDesignProps) => {
  const steps = [
    ['1', 'Pick the designs', 'Note the placements you want from this lookbook — mix and match across surfaces.'],
    ['2', 'Confirm the run', 'Review the quantities, placements, stock, finish, and delivery details.'],
    ['3', 'Send to your printer', 'Email the verified production links and job brief straight from Activation.'],
  ];
  return (
    <div style={{ width: '100%', height: '100%', background: '#F4F7FB', display: 'flex', flexDirection: 'column', padding: 64 }}>
      <div style={{ color: brand.secondary, fontWeight: 700, letterSpacing: '0.24em', fontSize: 13, textTransform: 'uppercase', marginBottom: 12 }}>How it works</div>
      <h2 style={{ fontSize: 40, fontWeight: 800, color: INK, margin: '0 0 28px', letterSpacing: '-0.01em' }}>Three steps to printed.</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {steps.map(([n, h, b]) => (
          <div key={n} style={{ background: '#fff', borderRadius: 16, padding: '22px 24px', display: 'flex', gap: 18, alignItems: 'flex-start', boxShadow: '0 8px 24px rgba(15,23,42,.08)' }}>
            <div style={{ width: 44, height: 44, flex: 'none', borderRadius: '50%', background: brand.primary, color: '#fff', fontWeight: 800, fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{n}</div>
            <div>
              <div style={{ fontSize: 19, fontWeight: 700, color: INK }}>{h}</div>
              <div style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.5, marginTop: 3 }}>{b}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 28, background: brandGradient(brand, 135), color: '#fff', borderRadius: 16, padding: '22px 24px', fontSize: 16, fontWeight: 600, lineHeight: 1.5 }}>
        Every design carries your branding and points at {brand.name}. One scan opens the {targetLabel(brand)} from a phone.
      </div>
      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 14, paddingTop: 24 }}>
        <span style={{ fontWeight: 800, fontSize: 20, color: INK }}>{brand.name}</span>
        <PoweredByPill />
      </div>
    </div>
  );
};
